import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../auth-client";
import { Brand } from "../components/Brand";

const inputCls =
  "w-full h-11 px-3.5 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/60 transition-colors";

export function PasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const providerError = searchParams.get("error") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [email, setEmail] = useState("");
  const [working, setWorking] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [replacementRequested, setReplacementRequested] = useState(false);
  const [linkUnavailable, setLinkUnavailable] = useState(Boolean(providerError) || !token);
  const [error, setError] = useState("");
  const complete = searchParams.get("status") === "complete";

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (password.length < 15) {
      setError("Use a passphrase of at least 15 characters.");
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }

    setWorking(true);
    try {
      const result = await authClient.resetPassword({ newPassword: password, token });
      if (result.error) {
        setLinkUnavailable(true);
        navigate("/reset-password?error=INVALID_TOKEN", { replace: true });
        return;
      }
      navigate("/reset-password?status=complete", { replace: true });
    } catch {
      setError("Your password could not be updated. Check your connection and try again.");
    } finally {
      setWorking(false);
    }
  };

  const requestReplacement = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Enter the email address linked to your account.");
      return;
    }

    setRequesting(true);
    try {
      const result = await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: "/reset-password",
      });
      if (result.error) {
        setError("A replacement link could not be requested. Please try again.");
        return;
      }
      setReplacementRequested(true);
    } catch {
      setError("A replacement link could not be requested. Check your connection and try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px]">
        <div className="flex justify-center mb-8">
          <Brand logoHeight={62} align="center" />
        </div>

        <div className="bg-surface-white border border-default rounded-xl px-[30px] py-[30px]">
          {complete ? (
            <div role="status" className="flex flex-col items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[rgba(86,205,174,0.14)] text-status-green flex items-center justify-center">
                <i className="fa-solid fa-check" aria-hidden="true" />
              </span>
              <div>
                <h1 className="text-title-md font-bold text-primary">Password updated</h1>
                <p className="text-body text-secondary mt-1">
                  Your new password has been saved. You can now sign in to FMC Leads Manager.
                </p>
              </div>
              <Link
                to="/login"
                className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold flex items-center justify-center"
              >
                Continue to sign in
              </Link>
              <Link
                to="/admin/login"
                className="w-full text-center text-caption font-semibold text-brand-red hover:underline"
              >
                FMC administrator sign in
              </Link>
            </div>
          ) : linkUnavailable ? (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col items-start gap-4" role="alert">
                <span className="w-10 h-10 rounded-full bg-brand-red-tint text-brand-red flex items-center justify-center">
                  <i className="fa-solid fa-link-slash" aria-hidden="true" />
                </span>
                <div>
                  <h1 className="text-title-md font-bold text-primary">Reset link unavailable</h1>
                  <p className="text-body text-secondary mt-1">
                    This link is invalid, has expired or has already been used. Request a fresh
                    24-hour link below.
                  </p>
                </div>
              </div>

              {replacementRequested ? (
                <div role="status" className="rounded-md bg-[rgba(86,205,174,0.1)] px-3.5 py-3">
                  <p className="text-caption font-semibold text-primary">Check your email</p>
                  <p className="text-caption text-secondary mt-0.5">
                    If the account exists, a new password-reset link has been sent.
                  </p>
                </div>
              ) : (
                <form onSubmit={requestReplacement} className="flex flex-col gap-4">
                  <label className="flex flex-col gap-1.5 text-caption font-medium text-primary">
                    Email address
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={requesting}
                    aria-busy={requesting}
                    className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold disabled:opacity-50"
                  >
                    {requesting ? "Requesting…" : "Send a new reset link"}
                  </button>
                </form>
              )}

              {error && (
                <div
                  role="alert"
                  className="rounded-xs bg-[rgba(255,101,80,0.1)] px-3 py-2.5 text-caption text-status-red"
                >
                  {error}
                </div>
              )}
              <Link to="/login" className="text-body font-semibold text-brand-red hover:underline">
                Return to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div>
                <h1 className="text-title-md font-bold text-primary">Choose a new password</h1>
                <p className="text-body text-secondary mt-1">
                  Enter a new password for your FMC Leads Manager account.
                </p>
              </div>
              <label className="flex flex-col gap-1.5 text-caption font-medium text-primary">
                New password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={15}
                  maxLength={128}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-caption font-medium text-primary">
                Confirm password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={15}
                  maxLength={128}
                  required
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className={inputCls}
                />
              </label>
              <p className="text-micro text-tertiary">
                Use at least 15 characters. Common or breached passwords are rejected.
              </p>
              {error && (
                <div
                  role="alert"
                  className="rounded-xs bg-[rgba(255,101,80,0.1)] px-3 py-2.5 text-caption text-status-red"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={working}
                aria-busy={working}
                className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold disabled:opacity-50"
              >
                {working ? "Saving…" : "Save new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
